import jsPDF from 'jspdf';

/**
 * Generate a professional certificate PDF with QR code
 * @param {Object} cert - Certificate data from API
 */
export async function generateCertificatePDF(cert) {
  const doc = new jsPDF({ orientation: 'landscape', unit: 'mm', format: 'a4' });
  const W = 297;
  const H = 210;

  // Colors — New palette
  const navy = [30, 58, 95];      // #2a3e5d
  const blue = [59, 130, 246];    // #20acf6
  const teal = [20, 184, 166];    // #02d1d4
  const darkText = [51, 65, 85];  // #1e293b
  const lightText = [100, 116, 139];

  // ========= BACKGROUND =========
  doc.setFillColor(...navy);
  doc.rect(0, 0, W, H, 'F');

  // Inner white card
  const margin = 12;
  doc.setFillColor(255, 255, 255);
  doc.roundedRect(margin, margin, W - margin * 2, H - margin * 2, 4, 4, 'F');

  // Top decorative bar
  doc.setFillColor(...blue);
  doc.rect(margin, margin, W - margin * 2, 6, 'F');

  // Bottom decorative bar
  doc.setFillColor(...teal);
  doc.rect(margin, H - margin - 3, W - margin * 2, 3, 'F');

  // ========= HEADER =========
  const contentLeft = 24;
  const contentRight = W - 24;
  let y = 34;

  // "Common Impact E-learning" logo text
  doc.setFontSize(10);
  doc.setTextColor(...blue);
  doc.setFont('helvetica', 'bold');
  doc.text('COMMON IMPACT E-LEARNING', contentLeft, y);

  // Accreditation badge (top right)
  if (cert.is_accredited) {
    doc.setFontSize(8);
    doc.setFillColor(...teal);
    const badgeW = 42;
    const badgeH = 8;
    const badgeX = contentRight - badgeW;
    doc.roundedRect(badgeX, y - 6, badgeW, badgeH, 2, 2, 'F');
    doc.setTextColor(...navy);
    doc.text('ACCREDITED COURSE', badgeX + badgeW / 2, y - 1, { align: 'center' });
  }

  // Divider line
  y += 8;
  doc.setDrawColor(...blue);
  doc.setLineWidth(0.3);
  doc.line(contentLeft, y, contentRight, y);

  // ========= TITLE =========
  y += 14;
  doc.setFontSize(28);
  doc.setTextColor(...navy);
  doc.setFont('helvetica', 'bold');
  doc.text('Certificate of Completion', W / 2, y, { align: 'center' });

  // ========= SUBTITLE =========
  y += 10;
  doc.setFontSize(11);
  doc.setTextColor(...lightText);
  doc.setFont('helvetica', 'normal');
  doc.text('This is to certify that', W / 2, y, { align: 'center' });

  // ========= LEARNER NAME =========
  y += 14;
  doc.setFontSize(24);
  doc.setTextColor(...darkText);
  doc.setFont('helvetica', 'bold');
  doc.text(cert.user_name || 'Learner Name', W / 2, y, { align: 'center' });

  // Name underline
  y += 3;
  doc.setDrawColor(...teal);
  doc.setLineWidth(0.5);
  const nameWidth = doc.getTextWidth(cert.user_name || 'Learner Name');
  doc.line(W / 2 - nameWidth / 2 - 5, y, W / 2 + nameWidth / 2 + 5, y);

  // ========= BODY =========
  y += 10;
  doc.setFontSize(11);
  doc.setTextColor(...lightText);
  doc.setFont('helvetica', 'normal');
  doc.text('has successfully completed the course', W / 2, y, { align: 'center' });

  // ========= COURSE TITLE =========
  y += 12;
  doc.setFontSize(18);
  doc.setTextColor(...navy);
  doc.setFont('helvetica', 'bold');
  const courseTitle = cert.course_title || 'Course Title';
  const titleLines = doc.splitTextToSize(courseTitle, 200);
  doc.text(titleLines, W / 2, y, { align: 'center' });
  y += titleLines.length * 8;

  // Organisation
  if (cert.organisation_name) {
    y += 4;
    doc.setFontSize(10);
    doc.setTextColor(...lightText);
    doc.setFont('helvetica', 'normal');
    doc.text(`Organisation: ${cert.organisation_name}`, W / 2, y, { align: 'center' });
  }

  // ========= DATE =========
  y += 10;
  const dateStr = cert.completion_date
    ? new Date(cert.completion_date).toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' })
    : '';
  doc.setFontSize(10);
  doc.setTextColor(...lightText);
  doc.setFont('helvetica', 'normal');
  doc.text(`Issued: ${dateStr}`, W / 2, y, { align: 'center' });

  // ========= FOOTER SECTION =========
  const footerY = H - margin - 30;

  // Signature line (left)
  doc.setDrawColor(...navy);
  doc.setLineWidth(0.3);
  doc.line(contentLeft + 10, footerY, contentLeft + 70, footerY);
  doc.setFontSize(8);
  doc.setTextColor(...lightText);
  doc.text('Authorised Signatory', contentLeft + 10, footerY + 5);
  doc.text('Common Impact E-learning', contentLeft + 10, footerY + 10);

  // Certificate number (center)
  doc.setFontSize(8);
  doc.setTextColor(...lightText);
  doc.setFont('courier', 'normal');
  doc.text(`Certificate No: ${cert.certificate_number}`, W / 2, footerY + 5, { align: 'center' });
  doc.setFont('helvetica', 'normal');
  doc.text('This certificate is a permanent record and does not expire.', W / 2, footerY + 10, { align: 'center' });

  // QR Code (right)
  if (cert.qr_code_base64) {
    try {
      const qrImg = `data:image/png;base64,${cert.qr_code_base64}`;
      doc.addImage(qrImg, 'PNG', contentRight - 30, footerY - 18, 25, 25);
      doc.setFontSize(6);
      doc.text('Scan to verify', contentRight - 17.5, footerY + 10, { align: 'center' });
    } catch (e) {
      console.warn('QR code embed failed:', e);
    }
  }

  // ========= SAVE =========
  const safeName = (cert.user_name || 'certificate').replace(/[^a-zA-Z0-9]/g, '_');
  doc.save(`${safeName}_${cert.certificate_number}.pdf`);
}
